import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { IBatchEventLog } from 'src/app/models/batch-event-log';
import { IGenericResult, ILot, ITransferAutomaticBatchExecution, ITransferBatchMassCreatePreview, ITransferBatchMassCreateResult } from 'src/app/models/generic';
import { environment } from 'src/environments/environment';

import { IAnnex, IInvoiceInfo, IRpaInfo } from '../../models/annex';
import { IEmailPreview } from '../../models/email';
import { IFilterBatchSearch } from '../../models/filters';
import { IBatchEmailConfig, ISendBatchEmail } from '../../models/generic';
import { ILogProcessSale } from '../../models/log-process-sale';
import { SHOW_LOADER } from '../loader-interceptor.service';
import { SnackbarService } from '../snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class ApiLotsService {
  baseUrls: { [key: string]: string } = {
    /*baseUrlBatchTransfer: `${environment.apis.transfers.baseUrl}/${environment.apis.transfers.microservices.batch}`,*/
    baseUrlBatchTransfer: `${environment.apis.unified.baseUrl}/${environment.apis.unified.microservices.transferBatch}`,
    //baseUrlBatchInvoice: `${environment.apis.invoices.baseUrl}/${environment.apis.invoices.microservices.batch}`
    baseUrlBatchInvoice: `${environment.apis.unified.baseUrl}/${environment.apis.unified.microservices.invoiceBatch}`
  };

  updateTemplateValuesEvent = new Subject<IInvoiceInfo>();
  updateTemplateValuesEventRpa = new Subject<IRpaInfo>();

  context = new HttpContext().set(SHOW_LOADER, false);

  maxFileSize: number = 5120; //5mb

  constructor(
    private _http: HttpClient,
    private _snackbarService: SnackbarService
  ) { }

  getLotsInvoicing(
    searchData: IFilterBatchSearch
  ): Observable<IGenericResult<ILot[]>> {
    delete searchData.BatchStatus;
    return this._http.post<IGenericResult<ILot[]>>(
      `${this.baseUrls['baseUrlBatchInvoice']}/list/batchs`,
      searchData
    );
  }

  getLotsInvoicesById(
    id: string,
    status: boolean
  ): Observable<IGenericResult<ILot>> {
    return this._http.get<IGenericResult<ILot>>(
      `${this.baseUrls['baseUrlBatchInvoice']}/batch/${id}?current=${status}`
    );
  }

  getLotsTransfer(
    searchData: IFilterBatchSearch
  ): Observable<IGenericResult<ILot[]>> {
    return this._http.post<IGenericResult<ILot[]>>(
      `${this.baseUrls['baseUrlBatchTransfer']}/list`,
      searchData
    );
  }

  getLotTransfersById(
    id: string,
    status: boolean,
    skipGlobalErrorHandler: boolean = false,
    showLoader: boolean = true
  ): Observable<IGenericResult<ILot>> {
    const headers = skipGlobalErrorHandler
      ? new HttpHeaders({ 'X-Skip-Error-Handler': 'true' })
      : undefined;

    return this._http.get<IGenericResult<ILot>>(
      `${this.baseUrls['baseUrlBatchTransfer']}/batch/${id}?status=${status}`,
      { headers, context: showLoader ? undefined : this.context }
    );
  }

  getInvoiceFile(
    id: string,
    batchType: 'invoice' | 'transfer'
  ): Observable<any> {
    const batchUrl: string = this.createUriByBatchType(id, batchType);
    return this._http.get(batchUrl, {
      responseType: 'blob',
      context: this.context
    });
  }

  createUriByBatchType(id: string, batchType: 'invoice' | 'transfer'): string {
    if (batchType === 'invoice') {
      return `${environment.apis.unified.baseUrl}/Invoice/download/${id}`;
    } else {
      return `${this.baseUrls['baseUrlBatchTransfer']}/${id}/invoice`;
    }
  }

  postInvoiceFile(id: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this._http.post(
      `${this.baseUrls['baseUrlBatchTransfer']}/${id}/invoice`,
      formData
    );
  }

  postRpaFile(id: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this._http.post(
      `${this.baseUrls['baseUrlBatchTransfer']}/${id}/rpa`,
      formData
    );
  }

  deleteFile(id: string): Observable<any> {
    return this._http.delete(
      `${this.baseUrls['baseUrlBatchTransfer']}/batch/annex/${id}`
    );
  }

  getCalculationFile(id: string): Observable<any> {
    return this._http.get(
      `${this.baseUrls['baseUrlBatchTransfer']}/${id}/calculations`,
      { responseType: 'blob', context: this.context }
    );
  }

  generateRpaFile(id: string): Observable<any> {
    let url: string = `${this.baseUrls['baseUrlBatchTransfer']}/${id}/generaterpa`;

    return this._http.get(url, { responseType: 'blob', context: this.context });
  }

  getRpaFile(id: string, batchType: 'invoice' | 'transfer'): Observable<any> {
    const batchUrl: string = this.createUriByBatchTypeRpa(id, batchType);
    return this._http.get(batchUrl, {
      responseType: 'blob',
      context: this.context
    });
  }

  getPaymentProof(id: string): Observable<any> {
    const batchUrl: string = `${this.baseUrls['baseUrlBatchTransfer']}/${id}/payment-proof`;
    return this._http.get(batchUrl, {
      responseType: 'blob',
      context: this.context
    });
  }

  createUriByBatchTypeRpa(
    id: string,
    batchType: 'invoice' | 'transfer'
  ): string {
    if (batchType === 'invoice') {
      return `${environment.apis.unified.baseUrl}/rpa/download/${id}`;
    } else {
      return `${this.baseUrls['baseUrlBatchTransfer']}/${id}/rpa`;
    }
  }

  postInvoiceInfo(invoice: IInvoiceInfo): Observable<null> {
    return this._http.post<null>(
      `${this.baseUrls['baseUrlBatchTransfer']}/${invoice.BatchId}/invoiceInfo`,
      invoice
    );
  }

  postRpaInfo(rpa: IRpaInfo): Observable<null> {
    return this._http.post<null>(
      `${this.baseUrls['baseUrlBatchTransfer']}/${rpa.BatchId}/rpaInfo`,
      rpa
    );
  }

  postCalcularIss(batchId: string, calcularIss: boolean): Observable<null> {
    return this._http.post<null>(
      `${this.baseUrls['baseUrlBatchTransfer']}/${batchId}/iss`,
      { BatchId: batchId, CalcularIss: calcularIss }
    );
  }

  postServiceCode(batchId: string, serviceCodeId: string | null): Observable<null> {
    return this._http.post<null>(
      `${this.baseUrls['baseUrlBatchTransfer']}/${batchId}/service-code`,
      { BatchId: batchId, ServiceCodeId: serviceCodeId },
      { context: this.context }
    );
  }

  validateFile(target: HTMLInputElement): {
    file: any;
    fileName: string;
  } {
    if (!target.files?.length) {
      return { file: '', fileName: '' };
    }

    const sizeFile = Math.round(target.files[0].size / 1024);

    if (sizeFile > 5120) {
      this._snackbarService.openSnackBar(
        'O arquivo ' + target.files[0].name + ' excede o tamanho de 5MB',
        'danger',
        5000
      );
    }

    if (!/.*\/pdf$/.test(target.files[0].type)) {
      this._snackbarService.openSnackBar(
        'Formato incompatível, por favor insira um arquivo PDF',
        'danger',
        5000
      );
      return { file: '', fileName: '' };
    }

    if (sizeFile > this.maxFileSize) {
      this._snackbarService.openSnackBar(
        'Arquivo muito grande, insira um arquivo até 5mb.',
        'danger',
        5000
      );
      return { file: '', fileName: '' };
    }

    const file = target.files[0];
    const fileName = target.files[0].name;

    return { file, fileName };
  }

  sendInvoiceBatchEmail(batches: ISendBatchEmail): Observable<any> {
    return this._http.post<any>(
      `${this.baseUrls['baseUrlBatchInvoice']}/send-batch-email`,
      batches
    );
  }

  createAutomaticTransferBatches(): Observable<IGenericResult<ITransferBatchMassCreateResult>> {
    return this._http.post<IGenericResult<ITransferBatchMassCreateResult>>(
      `${this.baseUrls['baseUrlBatchTransfer']}/manual-batch/add-all-contracts`,
      {}
    );
  }

  getAutomaticTransferBatchesPreview(): Observable<IGenericResult<ITransferBatchMassCreatePreview>> {
    return this._http.get<IGenericResult<ITransferBatchMassCreatePreview>>(
      `${this.baseUrls['baseUrlBatchTransfer']}/manual-batch/add-all-contracts/preview`
    );
  }

  // Dispara a geracao automatica de lotes em background (Hangfire) - nao espera
  // o processamento terminar, so confirma que foi enfileirado (ou que ja tinha
  // uma execucao em andamento). Acompanhamento via getAutomaticTransferBatchExecution.
  triggerAutomaticTransferBatches(): Observable<IGenericResult<ITransferAutomaticBatchExecution>> {
    return this._http.post<IGenericResult<ITransferAutomaticBatchExecution>>(
      `${this.baseUrls['baseUrlBatchTransfer']}/manual-batch/add-all-contracts/execution`,
      {}
    );
  }

  // Sem SHOW_LOADER:false aqui o overlay de carregamento global piscaria a
  // cada 3s durante o polling do acompanhamento - a barra de progresso do
  // proprio modal já cobre esse feedback visual.
  getAutomaticTransferBatchExecution(executionId: string): Observable<IGenericResult<ITransferAutomaticBatchExecution>> {
    return this._http.get<IGenericResult<ITransferAutomaticBatchExecution>>(
      `${this.baseUrls['baseUrlBatchTransfer']}/manual-batch/add-all-contracts/execution/${executionId}`,
      { context: this.context }
    );
  }

  getLatestAutomaticTransferBatchExecution(): Observable<IGenericResult<ITransferAutomaticBatchExecution | null>> {
    return this._http.get<IGenericResult<ITransferAutomaticBatchExecution | null>>(
      `${this.baseUrls['baseUrlBatchTransfer']}/manual-batch/add-all-contracts/execution/latest`,
      { context: this.context }
    );
  }

  sendTransferBatchEmail(batchId: string, userMsg?: string): Observable<any> {
    return this._http.post<any>(
      `${this.baseUrls['baseUrlBatchTransfer']}/send-notification/${batchId}`,
      { userMsg }
    );
  }

  sendTransferEstimatedPaymentBatchEmail(batchId: string): Observable<any> {
    return this._http.post<any>(
      `${this.baseUrls['baseUrlBatchTransfer']}/send-estimated-payment-notification/${batchId}`,
      {}
    );
  }

  /** Prévia dos e-mails que "E-mail Lote" (ou "E-mail Pgto", com `paymentEmail`) enviaria, sem enviar. */
  getTransferBatchEmailPreview(batchId: string, paymentEmail: boolean): Observable<IEmailPreview[]> {
    const route = paymentEmail ? 'estimated-payment-notification-preview' : 'notification-preview';

    // Sem o loader global: o modal de prévia mostra o próprio carregamento por aba.
    return this._http.get<IEmailPreview[]>(
      `${this.baseUrls['baseUrlBatchTransfer']}/${route}/${batchId}`,
      { context: this.context }
    );
  }

  getBatchEmailConfig(): Observable<IGenericResult<IBatchEmailConfig[]>> {
    return this._http.get<IGenericResult<IBatchEmailConfig[]>>(
      `${this.baseUrls['baseUrlBatchInvoice']}/bank-email-config`
    );
  }

  cancelAllContractsBatch(
    batchId: string,
    createNewBatchAfterCancel = false
  ): Observable<IGenericResult<any>> {
    return this._http.post<IGenericResult<any>>(
      `${this.baseUrls['baseUrlBatchInvoice']}/cancel-batch/${batchId}?createNewBatch=${createNewBatchAfterCancel}`,
      {}
    );
  }

  saveBatchEmailConfig(
    config: IBatchEmailConfig
  ): Observable<IGenericResult<IBatchEmailConfig>> {
    return this._http.post<IGenericResult<IBatchEmailConfig>>(
      `${this.baseUrls['baseUrlBatchInvoice']}/bank-email-config`,
      config
    );
  }

  getInvoiceBatchEventLog(
    batchNumber: string,
    batchId?: number
  ): Observable<any> {
    return this._http.get<IGenericResult<IBatchEventLog[]>>(
      `${this.baseUrls['baseUrlBatchInvoice']}/event-logs?batchNumber=${batchNumber}&batchId=${batchId}`
    );
  }

  getInvoiceBatchEventSapLog(batchId?: any): Observable<any> {
    return this._http.get<IGenericResult<ILogProcessSale[]>>(
      `${this.baseUrls['baseUrlBatchInvoice']}/event-sap-logs?batchId=${batchId}`
    );
  }

  getTransferBatchEventLog(
    batchNumber: string,
    batchId?: number
  ): Observable<any> {
    return this._http.get<IGenericResult<IBatchEventLog[]>>(
      `${this.baseUrls['baseUrlBatchTransfer']}/event-logs?batchNumber=${batchNumber}&batchId=${batchId}`
    );
  }

  getTransferBatchEventSapLog(batchId?: any): Observable<any> {
    return this._http.get<IGenericResult<ILogProcessSale[]>>(
      `${this.baseUrls['baseUrlBatchTransfer']}/event-sap-logs?batchId=${batchId}`
    );
  }
}
